import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import {
  IReEnrollmentCyclesResponse,
  IReEnrollmentCycleResponseById,
  IReEnrollmentState,
  IActivateReEnrollmentCycleResponse,
  IFinalizeReEnrollmentCycleResponse,
} from '../../interface/reEnrollment.interface';
import {
  fetchReEnrollmentCycle,
  fetchReEnrollmentCycles,
  activateReEnrollmentCycle,
  finalizeReEnrollmentCycle,
} from '../action/reEnrollment.action';

const initialState: IReEnrollmentState = {
  cycles: [],
  currentCycle: null,
  pagination: null,
  loading: false,
  isActivating: false,
  error: null,
};

const reEnrollmentSlice = createSlice({
  name: 'reEnrollment',
  initialState,
  reducers: {
    resetCurrentCycle: (state) => {
      state.currentCycle = null;
    },
    resetCycles: (state) => {
      state.cycles = [];
      state.pagination = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all re-enrollment cycles
      .addCase(fetchReEnrollmentCycles.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.cycles = [];
        state.pagination = null;
      })
      .addCase(
        fetchReEnrollmentCycles.fulfilled,
        (state, action: PayloadAction<IReEnrollmentCyclesResponse>) => {
          state.loading = false;
          state.cycles = action.payload.data.cycles;
          state.pagination = {
            page: action.payload.data.page,
            pageSize: action.payload.data.pageSize,
            total: action.payload.data.total,
            totalPages: action.payload.data.totalPages,
          };
        },
      )
      .addCase(fetchReEnrollmentCycles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_re_enrollment_cycles', 'Failed to fetch re-enrollment cycles.');
        state.cycles = [];
        state.pagination = null;
      })
      // Fetch single re-enrollment cycle
      .addCase(fetchReEnrollmentCycle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentCycle = null;
      })
      .addCase(
        fetchReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IReEnrollmentCycleResponseById>) => {
          state.loading = false;
          state.currentCycle = action.payload.data.cycle;
          state.error = null;
        },
      )
      .addCase(fetchReEnrollmentCycle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || i18n.t('toast.failed_to_fetch_re_enrollment_cycle', 'Failed to fetch re-enrollment cycle.');
        state.currentCycle = null;
      })
      // Activate re-enrollment cycle
      .addCase(activateReEnrollmentCycle.pending, (state) => {
        state.isActivating = true;
        state.error = null;
      })
      .addCase(
        activateReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IActivateReEnrollmentCycleResponse>) => {
          state.isActivating = false;
          // Update cycle in cycles list
          const index = state.cycles.findIndex(
            (cycle) => cycle.id === action.payload.data.cycle.id,
          );
          if (index !== -1) {
            state.cycles[index] = {
              ...state.cycles[index],
              status: action.payload.data.cycle.status,
            };
          }
          state.error = null;
        },
      )
      .addCase(activateReEnrollmentCycle.rejected, (state, action) => {
        state.isActivating = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_activate_re_enrollment_cycle', 'Failed to activate re-enrollment cycle.');
      })
      // Finalize re-enrollment cycle
      .addCase(finalizeReEnrollmentCycle.pending, (state) => {
        state.isActivating = true;
        state.error = null;
      })
      .addCase(
        finalizeReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IFinalizeReEnrollmentCycleResponse>) => {
          state.isActivating = false;
          // Update cycle in cycles list
          const index = state.cycles.findIndex(
            (cycle) => cycle.id === action.payload.data.cycle.id,
          );
          if (index !== -1) {
            state.cycles[index] = {
              ...state.cycles[index],
              status: action.payload.data.cycle.status,
            };
          }
          // Optionally update currentCycle
          if (state.currentCycle && state.currentCycle.id === action.payload.data.cycle.id) {
            state.currentCycle = {
              ...state.currentCycle,
              status: action.payload.data.cycle.status,
            };
          }
          state.error = null;
        },
      )
      .addCase(finalizeReEnrollmentCycle.rejected, (state, action) => {
        state.isActivating = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_finalize_re_enrollment_cycle', 'Failed to finalize re-enrollment cycle.');
      });
  },
});

export const { resetCurrentCycle, resetCycles, clearError } = reEnrollmentSlice.actions;

export default reEnrollmentSlice.reducer;
